import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridToolbar,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import { useFreelancerExportList } from "../../hooks/useFreelancerExport";
import {
  FreelancerExportSearchRequest,
  exportFreelancersToExcel,
} from "../../api/freelancer-export-api";

const ONBOARDING_STATUS_OPTIONS = [
  "NotSubmitted",
  "Submitted",
  "Interview",
  "SoftSkillsEvaluation",
  "HardSkillsEvaluation",
  "Positioning",
  "Accepted",
  "Refused",
];

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

const FreelancerExportDashboard = () => {
  const { t } = useTranslation();

  // Filter state
  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Export language state
  const [exportLanguage, setExportLanguage] = useState<string>("Fr");

  // Pagination & sorting state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Export loading state
  const [isExporting, setIsExporting] = useState(false);

  // Build search request
  const searchRequest: FreelancerExportSearchRequest = useMemo(
    () => ({
      page: paginationModel.page + 1, // API is 1-based
      pageSize: paginationModel.pageSize,
      name: nameFilter || undefined,
      onboardingStatus: statusFilter || undefined,
    }),
    [paginationModel, nameFilter, statusFilter]
  );

  const { data, isLoading } = useFreelancerExportList(searchRequest);

  // Translation helpers for DataGrid display
  const translateValue = useCallback(
    (value: string | null | undefined, prefix: string): string =>
      value ? t(`${prefix}.${value}`) : "",
    [t]
  );

  const translateList = useCallback(
    (value: string | null | undefined, prefix: string): string =>
      value
        ? value
            .split(", ")
            .map((item) => t(`${prefix}.${item}`))
            .join(", ")
        : "",
    [t]
  );

  const translateSkillsOrTools = useCallback(
    (value: string | null | undefined, namePrefix: string | null): string => {
      if (!value) return "";
      return value
        .split(", ")
        .map((entry) => {
          const match = entry.match(/^(.+?)\s*\((.+)\)$/);
          if (match) {
            const name = namePrefix
              ? t(`${namePrefix}.${match[1]}`)
              : match[1];
            const stage = t(`DataProcessStages.${match[2]}`);
            return `${name} (${stage})`;
          }
          return namePrefix ? t(`${namePrefix}.${entry}`) : entry;
        })
        .join(", ");
    },
    [t]
  );

  // Column visibility - hide detailed columns by default
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      personalAddressStreet: false,
      personalAddressPostalCode: false,
      personalAddressRegion: false,
      companyAddressStreet: false,
      companyAddressPostalCode: false,
      companyAddressCity: false,
      companyAddressRegion: false,
      companyAddressCountry: false,
      submittedDate: false,
      interviewDate: false,
      softSkillsEvaluationDate: false,
      hardSkillsEvaluationDate: false,
      positioningDate: false,
      acceptedDate: false,
      refusedDate: false,
      softSkillsEvaluationStatus: false,
      softSkillsInvitedAt: false,
      softSkillsCompletedAt: false,
      confirmedSkills: false,
      usedTools: false,
      fluentLanguages: false,
      operationalLanguages: false,
    });

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "freelancerProfileId", headerName: t("admin.export.id"), width: 70 },
      { field: "firstName", headerName: t("admin.export.firstName"), width: 130 },
      { field: "lastName", headerName: t("admin.export.lastName"), width: 130 },
      { field: "email", headerName: t("admin.export.email"), width: 220 },
      {
        field: "expertise",
        headerName: t("admin.export.expertise"),
        width: 160,
        valueFormatter: (value: string) => translateValue(value, "Expertises"),
      },
      { field: "expertiseCategory", headerName: t("admin.export.expertiseCategory"), width: 160 },
      {
        field: "experience",
        headerName: t("admin.export.experience"),
        width: 130,
        valueFormatter: (value: string) => translateValue(value, "Experiences"),
      },
      {
        field: "freelancingYearsOfExperience",
        headerName: t("admin.export.freelancingYears"),
        width: 140,
        valueFormatter: (value: string) =>
          translateValue(value, "FreelancingYearsOfExperiences"),
      },
      {
        field: "isTrainer",
        headerName: t("admin.export.isTrainer"),
        width: 100,
        type: "boolean",
      },
      { field: "dailyRateMin", headerName: t("admin.export.dailyRateMin"), width: 110, type: "number" },
      { field: "dailyRateMax", headerName: t("admin.export.dailyRateMax"), width: 110, type: "number" },
      { field: "dailyRateCurrency", headerName: t("admin.export.currency"), width: 90 },
      {
        field: "expertSkills",
        headerName: t("admin.export.expertSkills"),
        width: 250,
        valueFormatter: (value: string) => translateSkillsOrTools(value, "Skills"),
      },
      {
        field: "confirmedSkills",
        headerName: t("admin.export.confirmedSkills"),
        width: 250,
        valueFormatter: (value: string) => translateSkillsOrTools(value, "Skills"),
      },
      {
        field: "masteredTools",
        headerName: t("admin.export.masteredTools"),
        width: 250,
        valueFormatter: (value: string) => translateSkillsOrTools(value, null),
      },
      {
        field: "usedTools",
        headerName: t("admin.export.usedTools"),
        width: 250,
        valueFormatter: (value: string) => translateSkillsOrTools(value, null),
      },
      {
        field: "nativeLanguages",
        headerName: t("admin.export.nativeLanguages"),
        width: 160,
        valueFormatter: (value: string) => translateList(value, "Languages"),
      },
      {
        field: "fluentLanguages",
        headerName: t("admin.export.fluentLanguages"),
        width: 160,
        valueFormatter: (value: string) => translateList(value, "Languages"),
      },
      {
        field: "operationalLanguages",
        headerName: t("admin.export.operationalLanguages"),
        width: 160,
        valueFormatter: (value: string) => translateList(value, "Languages"),
      },
      {
        field: "regions",
        headerName: t("admin.export.regions"),
        width: 180,
        valueFormatter: (value: string) => translateList(value, "Regions"),
      },
      {
        field: "sectors",
        headerName: t("admin.export.sectors"),
        width: 180,
        valueFormatter: (value: string) => translateList(value, "Sectors"),
      },
      {
        field: "businessDomains",
        headerName: t("admin.export.businessDomains"),
        width: 180,
        valueFormatter: (value: string) => translateList(value, "BusinessDomains"),
      },
      { field: "personalAddressStreet", headerName: t("admin.export.personalStreet"), width: 200 },
      { field: "personalAddressPostalCode", headerName: t("admin.export.personalPostalCode"), width: 120 },
      { field: "personalAddressCity", headerName: t("admin.export.personalCity"), width: 140 },
      { field: "personalAddressRegion", headerName: t("admin.export.personalRegion"), width: 140 },
      { field: "personalAddressCountry", headerName: t("admin.export.personalCountry"), width: 140 },
      { field: "companyLegalName", headerName: t("admin.export.companyName"), width: 180 },
      {
        field: "isPortageSalarial",
        headerName: t("admin.export.isPortageSalarial"),
        width: 130,
        type: "boolean",
      },
      { field: "companyCountry", headerName: t("admin.export.companyCountry"), width: 140 },
      { field: "companyAddressStreet", headerName: t("admin.export.companyStreet"), width: 200 },
      { field: "companyAddressPostalCode", headerName: t("admin.export.companyPostalCode"), width: 120 },
      { field: "companyAddressCity", headerName: t("admin.export.companyCity"), width: 140 },
      { field: "companyAddressRegion", headerName: t("admin.export.companyRegion"), width: 140 },
      { field: "companyAddressCountry", headerName: t("admin.export.companyAddressCountry"), width: 140 },
      {
        field: "onboardingStatus",
        headerName: t("admin.export.onboardingStatus"),
        width: 160,
        valueFormatter: (value: string) =>
          translateValue(value, "enums.FreelancerOnboardingStatus"),
      },
      {
        field: "onboardingStartDate",
        headerName: t("admin.export.onboardingStartDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "submittedDate",
        headerName: t("admin.export.submittedDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "interviewDate",
        headerName: t("admin.export.interviewDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "softSkillsEvaluationDate",
        headerName: t("admin.export.softSkillsDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "hardSkillsEvaluationDate",
        headerName: t("admin.export.hardSkillsDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "positioningDate",
        headerName: t("admin.export.positioningDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "acceptedDate",
        headerName: t("admin.export.acceptedDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "refusedDate",
        headerName: t("admin.export.refusedDate"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "softSkillsEvaluationStatus",
        headerName: t("admin.export.softSkillsStatus"),
        width: 160,
        valueFormatter: (value: string) => {
          if (value === "Invited") return t("admin.export.softSkillsInvited");
          if (value === "Completed") return t("admin.export.softSkillsCompleted");
          return value ?? "";
        },
      },
      {
        field: "softSkillsInvitedAt",
        headerName: t("admin.export.softSkillsInvitedAt"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "softSkillsCompletedAt",
        headerName: t("admin.export.softSkillsCompletedAt"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      {
        field: "updatedAt",
        headerName: t("admin.export.updatedAt"),
        width: 160,
        valueFormatter: (value: string) => formatDate(value),
      },
      { field: "updatedByName", headerName: t("admin.export.updatedBy"), width: 160 },
    ],
    [t, translateValue, translateList, translateSkillsOrTools]
  );

  const handleExportExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const blob = await exportFreelancersToExcel(
        {
          name: nameFilter || undefined,
          onboardingStatus: statusFilter || undefined,
        },
        exportLanguage
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `freelancers-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  }, [nameFilter, statusFilter, exportLanguage]);

  return (
    <Box px={6} py={4}>
      <Typography variant="h4" mb={3}>
        {t("admin.export.title")}
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          label={t("admin.export.searchName")}
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <Autocomplete
          size="small"
          options={ONBOARDING_STATUS_OPTIONS}
          value={statusFilter}
          onChange={(_, newValue) => setStatusFilter(newValue)}
          getOptionLabel={(option) =>
            t(`enums.FreelancerOnboardingStatus.${option}`)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.export.onboardingStatus")}
            />
          )}
          sx={{ minWidth: 220 }}
        />
        <ToggleButtonGroup
          value={exportLanguage}
          exclusive
          onChange={(_, value) => {
            if (value) setExportLanguage(value);
          }}
          size="small"
          sx={{ height: 40 }}
        >
          <ToggleButton value="Fr" sx={{ px: 0.75, fontSize: "0.75rem", minWidth: 28 }}>FR</ToggleButton>
          <ToggleButton value="En" sx={{ px: 0.75, fontSize: "0.75rem", minWidth: 28 }}>EN</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={
            isExporting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={handleExportExcel}
          disabled={isExporting}
          sx={{
            backgroundColor: zoullooColors.secondary.main,
            color: zoullooColors.secondary.contrastText,
            "&:hover": {
              backgroundColor: zoullooColors.secondary.dark,
            },
          }}
        >
          {t("admin.export.exportExcel")}
        </Button>
      </Box>

      {/* DataGrid */}
      <Box sx={{ height: "calc(100vh - 180px)", width: "100%" }}>
        <DataGrid
          rows={data?.items ?? []}
          columns={columns}
          rowCount={data?.totalCount ?? 0}
          loading={isLoading}
          getRowId={(row) => row.freelancerProfileId}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[25, 50, 100]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                sx: { order: -1 },
              },
            },
          }}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: zoullooColors.primary.light,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default FreelancerExportDashboard;
